import { useEffect } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import OrderBasketScreen from '../pages/OrderBasketScreen';
import PageNotFound from '../pages/PageNotFound';
import RestaurantDetailsScreen from '../pages/RestaurantDetailsScreen';
import { SCREENS } from '../services/exports/Constants';
import useScreenFallback from '../hooks/navigation/useScreenFallback';
import { RoutesInterface } from '../services/exports/Interfaces';
import TermsAndConditions from '../pages/legal/TermsAndConditions';
import PrivacyPolicy from '../pages/legal/PrivacyPolicy';
import CookiePolicy from '../pages/legal/CookiePolicy';
import GlobalAppUtility from '../services/utilities/GlobalAppUtility';
import ScrollToTop from '../components-library/navigation/ScrollToTop';
import CookieConsentSync from '../components-library/navigation/CookieConsentSync';
import GlobalModalsStack from '../modals/GlobalModalsStack';
import UploadGoogleReviewScreen from '../pages/UploadGoogleReviewScreen';
import RewardsScreen from '../pages/RewardsScreen';
import useAppState from '../hooks/global/useAppState';
import useAppMethods from '../hooks/utility/useAppMethods';
import Impressum from '../pages/legal/impressum';
import OrderDetailsScreen from '../pages/OrderDetailsScreen';
import OrderDraftPreloader from '../providers/OrderDraftPreloader';
import NhasUserTokenAuthenticator from '../providers/auth/NhasUserTokenAuthenticator';
import WindowResource from '../services/resources/WindowResource';
import AppliedOfferToast from '../features/offers/components/AppliedOfferToast';
import useScreenType from '../hooks/utility/useScreenType';
import useWatchOffers from '../features/offers/hooks/useWatchOffers';
import GiftCardsCheckoutScreen from '../pages/gift-cards/checkout/GiftCardsCheckoutScreen';
import GiftCardsOrderDetailsScreen from '../pages/gift-cards/order-details/GiftCardsOrderDetailsScreen';
import GiftCardTermsAndConditions from '../pages/gift-cards/terms-and-conditions/GiftCardTermsAndConditions';
import { AdyenRedirectResultHandler } from '../hooks/payment/useHandleAdyenRedirectResult';
import { SpreedlyRedirectResultHandler } from '../hooks/payment/useHandleSpreedlyRedirectResult';
import OrderConfirmationScreen from '../pages/checkout/OrderConfirmationScreen';
import OrderPaymentScreen from '../pages/checkout/OrderPaymentScreen';

function Router() {
  const screenFallback = useScreenFallback();
  const { isDesktop } = useScreenType();

  useWatchOffers();

  const companyRoutes: RoutesInterface[] = [
    {
      path: `${SCREENS.BASKET}`,
      element: <OrderBasketScreen />,
    },
    {
      path: `${SCREENS.ORDER_DETAILS}/:uuid`,
      element: <OrderDetailsScreen />,
    },
    {
      path: `${SCREENS.GIFT_CARDS_ORDER_DETAILS}/:uuid`,
      element: <GiftCardsOrderDetailsScreen />,
    },
    {
      path: SCREENS.CHECKOUT,
      element: <OrderConfirmationScreen />,
    },
    {
      path: `${SCREENS.PAYMENT}`,
      element: <OrderPaymentScreen />,
    },
    {
      path: SCREENS.GIFT_CARDS_CHECKOUT,
      element: <GiftCardsCheckoutScreen />,
    },
    {
      path: SCREENS.TERMS_AND_CONDITIONS,
      element: <TermsAndConditions />,
    },
    {
      path: SCREENS.GIFT_CARDS_TERMS_AND_CONDITIONS,
      element: <GiftCardTermsAndConditions />,
    },
    {
      path: SCREENS.PRIVACY_POLICY,
      element: <PrivacyPolicy />,
    },
    {
      path: SCREENS.COOKIE_POLICY,
      element: <CookiePolicy />,
    },
    {
      path: SCREENS.IMPRESSUM,
      element: <Impressum />,
    },
    {
      path: SCREENS.UPLOAD_GOOGLE_REVIEW_SCREEN,
      element: <UploadGoogleReviewScreen />,
      // protectedType: 'Auth',
    },
    {
      path: SCREENS.REWARDS_SCREEN,
      element: <RewardsScreen />,
    },
    {
      path: '*',
      element: <PageNotFound />,
    },
  ];

  const ROUTES: RoutesInterface[] = [
    {
      path: SCREENS.NOT_FOUND_SCREEN,
      element: <PageNotFound />,
    },
    ...companyRoutes,
    {
      path: ':slug',
      children: [
        {
          path: '',
          element: <RestaurantDetailsScreen />,
        },
        ...companyRoutes,
      ],
    },
    {
      path: SCREENS.HOME_SCREEN,
      element: <RestaurantDetailsScreen />,
    },
    {
      path: '*',
      element: <PageNotFound />,
    },
  ];

  const GlobalAppMethods = useAppMethods();
  const GlobalAppState = useAppState();

  GlobalAppUtility.setGlobalAppMethodsContext(GlobalAppMethods);

  useEffect(() => {
    GlobalAppUtility.setGlobalAppStateContext(GlobalAppState);
  }, [GlobalAppState]);

  return (
    <>
      <BrowserRouter basename={WindowResource.basePath}>
        <Routes>
          {ROUTES.map((route: RoutesInterface) => (
            <Route path={route.path} {...(route?.element ? { element: screenFallback(route) } : {})} key={route.path}>
              {route.children?.map((item) => (
                <Route path={item.path} element={screenFallback(item)} key={item.path} />
              ))}
            </Route>
          ))}
        </Routes>
        <ScrollToTop />
        <CookieConsentSync />
        <GlobalModalsStack />
        <OrderDraftPreloader />
        <NhasUserTokenAuthenticator />
        <AdyenRedirectResultHandler />
        <SpreedlyRedirectResultHandler />
        {isDesktop && <AppliedOfferToast />}
      </BrowserRouter>
    </>
  );
}

export default Router;
